import React, { useState, useEffect, useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Card, CardContent, CardHeader } from "../../ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  MagnifyingGlass,
  Funnel,
  Check,
  CalendarBlank,
  MapPin,
  GraduationCap,
  CaretLeft,
  CaretRight,
  EnvelopeSimple,
  Briefcase,
} from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useGetApprovedApplications } from "../../../adapters/Requests";
import ExportDropdown from "../../export";
import { DateRangeFilter } from "../../ui/daterangefilter";
import { Sheet, SheetContent } from "../../ui/sheet";
import AcceptanceDetailsViewer from "../../viewers/AcceptanceDetailsViewer";

export default function AcceptedTable({ internship_type }) {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const { data, isLoading, isFetching, isError } = useGetApprovedApplications(
    internship_type,
    { start_date: dateRange.from, end_date: dateRange.to },
  );

  const [selectedApplication, setSelectedApplication] = useState(null);

  const [globalFilter, setGlobalFilter] = useState("");
  const [batchFilter, setBatchFilter] = useState("ALL");
  const [locationFilter, setLocationFilter] = useState("ALL");
  const [unitFilter, setUnitFilter] = useState("ALL");
  const [sorting, setSorting] = useState([]);

  const [tableData, setTableData] = useState([]);
  const [uniqueBatches, setUniqueBatches] = useState([]);
  const [uniqueLocations, setUniqueLocations] = useState([]);
  const [uniqueUnits, setUniqueUnits] = useState([]);

  useEffect(() => {
    const rawData = Array.isArray(data) ? data : data?.data || [];
    if (rawData && rawData.length > 0) {
      const formatted = rawData
        .map((app) => ({
          id: app.id,
          firstName: `${app.Applicant.firstname} ${app.Applicant.lastname}`,
          ittype: app.vacancy.vacancy_position.name.toUpperCase(),
          status: app.status || "Unknown",
          state: app.Applicant.state,
          age:
            new Date().getFullYear() -
            new Date(app.Applicant.date_of_birth).getFullYear(),
          cos:
            app.ApplicationsAcademicQualification?.course?.name ||
            app.ApplicationNYSCDetails?.course?.name,
          date: new Date(app.created_at).toLocaleDateString(),
          batch: app.vacancy.batch.name,
          companylocation: app.vacancy.location.name,
          businessunit: app.vacancy.business_unit.name,
          original: app,
        }))
        .sort(
          (a, b) =>
            new Date(b.original.created_at) - new Date(a.original.created_at),
        );

      setTableData(formatted);
      setUniqueBatches([...new Set(formatted.map((item) => item.batch))]);
      setUniqueLocations([
        ...new Set(formatted.map((item) => item.companylocation)),
      ]);
      setUniqueUnits([...new Set(formatted.map((item) => item.businessunit))]);
    }
  }, [data]);

  const filteredData = useMemo(() => {
    return tableData.filter((item) => {
      const matchesSearch =
        globalFilter === "" ||
        Object.values(item).some(
          (val) =>
            typeof val === "string" &&
            val.toLowerCase().includes(globalFilter.toLowerCase()),
        );
      const matchesBatch = batchFilter === "ALL" || item.batch === batchFilter;
      const matchesLocation =
        locationFilter === "ALL" || item.companylocation === locationFilter;
      const matchesUnit =
        unitFilter === "ALL" || item.businessunit === unitFilter;

      return matchesSearch && matchesBatch && matchesLocation && matchesUnit;
    });
  }, [tableData, globalFilter, batchFilter, locationFilter, unitFilter]);

  const handleRowClick = (row) => {
    setSelectedApplication({
      id: row.id,
      type: row.ittype.toLowerCase(),
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "VERIFIED":
        return "warning";
      case "ACCEPTED":
        return "success";
      case "REJECTED":
        return "error";
      case "REVIEW":
        return "secondary";
      default:
        return "outline";
    }
  };

  const columns = [
    {
      header: "Applicant",
      accessorKey: "firstName",
      cell: ({ row }) => (
        <div className="flex flex-col py-1">
          <span className="font-bold text-gray-900 text-sm">
            {row.getValue("firstName")}
          </span>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded text-gray-600 font-medium border border-gray-200">
              {row.original.age} yrs
            </span>
            <span className="text-[10px] text-gray-500">
              {row.original.state}
            </span>
          </div>
        </div>
      ),
    },
    {
      header: "Type",
      accessorKey: "ittype",
      cell: ({ row }) => (
        <Badge
          variant="outline"
          className="font-semibold text-[10px] px-2 bg-slate-50"
        >
          {row.getValue("ittype")}
        </Badge>
      ),
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: ({ row }) => (
        <Badge
          variant={getStatusColor(row.getValue("status"))}
          className="font-semibold text-[10px] px-2"
        >
          {row.getValue("status")}
        </Badge>
      ),
    },
    {
      header: "Course",
      accessorKey: "cos",
      cell: ({ row }) => (
        <div
          className="flex items-center text-gray-700 text-xs font-medium max-w-[150px] truncate"
          title={row.getValue("cos")}
        >
          <GraduationCap className="mr-1.5 text-gray-400 shrink-0" size={14} />
          <span className="truncate">{row.getValue("cos")}</span>
        </div>
      ),
    },
    {
      header: "Placement",
      accessorKey: "businessunit",
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center text-gray-700 text-xs font-medium">
            <Briefcase className="mr-1.5 text-gray-400" size={14} />
            {row.getValue("businessunit")}
          </div>
          <div className="flex items-center text-gray-500 text-[11px]">
            <MapPin className="mr-1.5 text-gray-400" size={14} />
            {row.original.companylocation}
          </div>
        </div>
      ),
    },
    {
      header: "Date",
      accessorKey: "date",
      cell: ({ row }) => (
        <div className="flex items-center text-gray-500 text-xs">
          <CalendarBlank className="mr-1.5" size={14} />
          {row.getValue("date")}
        </div>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs gap-2 hover:bg-green-50 hover:text-green-700 hover:border-green-200"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(row.original);
          }}
        >
          <EnvelopeSimple size={14} />
          Details
        </Button>
      ),
    },
  ];

  const exportColumns = [
    { headerName: "Name", field: "firstName" },
    { headerName: "Type", field: "ittype" },
    { headerName: "Status", field: "status" },
    { headerName: "Age", field: "age" },
    { headerName: "State", field: "state" },
    { headerName: "Course", field: "cos" },
    { headerName: "Batch", field: "batch" },
    { headerName: "Location", field: "companylocation" },
    { headerName: "Unit", field: "businessunit" },
    { headerName: "Date", field: "date" },
  ];

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      globalFilter,
      sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
  });

  if (isLoading) {
    return (
      <Card className="w-full h-96 flex items-center justify-center border-slate-200">
        <Loader2 className="w-10 h-10 text-green-600 animate-spin" />
      </Card>
    );
  }

  if (isError) {
    return (
      <div className="p-8 text-center text-red-500">
        Error loading applications.
      </div>
    );
  }

  return (
    <>
      <Card className="w-full max-w-[95%] m-auto border-slate-200 shadow-sm bg-white mt-4">
        <CardHeader className="flex flex-col gap-4 p-4 border-b border-slate-100 bg-gray-50/40">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <DateRangeFilter
                dateRange={dateRange}
                setDateRange={setDateRange}
                minDate={data?.meta?.minDate}
              />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs border-dashed border-gray-300"
                  >
                    <Funnel className="mr-2 h-3 w-3" />
                    Batch: {batchFilter === "ALL" ? "All" : batchFilter}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-48 max-h-64 overflow-y-auto"
                >
                  <DropdownMenuItem onClick={() => setBatchFilter("ALL")}>
                    {batchFilter === "ALL" && (
                      <Check className="mr-2 h-3 w-3" />
                    )}{" "}
                    All Batches
                  </DropdownMenuItem>
                  {uniqueBatches.map((batch) => (
                    <DropdownMenuItem
                      key={batch}
                      onClick={() => setBatchFilter(batch)}
                    >
                      {batchFilter === batch && (
                        <Check className="mr-2 h-3 w-3" />
                      )}{" "}
                      {batch}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs border-dashed border-gray-300"
                  >
                    <MapPin className="mr-2 h-3 w-3" />
                    Loc: {locationFilter === "ALL" ? "All" : locationFilter}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-48 max-h-64 overflow-y-auto"
                >
                  <DropdownMenuItem onClick={() => setLocationFilter("ALL")}>
                    {locationFilter === "ALL" && (
                      <Check className="mr-2 h-3 w-3" />
                    )}{" "}
                    All Locations
                  </DropdownMenuItem>
                  {uniqueLocations.map((loc) => (
                    <DropdownMenuItem
                      key={loc}
                      onClick={() => setLocationFilter(loc)}
                    >
                      {locationFilter === loc && (
                        <Check className="mr-2 h-3 w-3" />
                      )}{" "}
                      {loc}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
              <div className="relative w-full sm:w-64">
                <MagnifyingGlass
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={14}
                />
                <Input
                  placeholder="Search applicants..."
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  className="pl-9 h-8 text-xs bg-white border-slate-200 focus:border-green-500 focus:ring-green-500"
                />
              </div>

              <ExportDropdown
                onExport={() => { }}
                data={filteredData}
                columns={exportColumns}
                className="h-8"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0 relative">
          {isFetching && !isLoading && (
            <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-green-600 animate-spin" />
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-gray-50/50">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="hover:bg-transparent border-b border-slate-100"
                  >
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className="h-9 text-[11px] font-bold text-gray-500 uppercase tracking-wider"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="hover:bg-green-50/30 transition-colors border-b border-slate-100 cursor-pointer group"
                      onClick={() => handleRowClick(row.original)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="py-3">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-32 text-center text-gray-500"
                    >
                      No applications found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>

        <div className="flex items-center justify-between p-2 px-4 border-t border-slate-100 bg-gray-50/30">
          <div className="text-[11px] text-gray-500">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="h-7 w-20 bg-white text-[11px]"
            >
              <CaretLeft className="mr-1" size={12} /> Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="h-7 w-20 bg-white text-[11px]"
            >
              Next <CaretRight className="ml-1" size={12} />
            </Button>
          </div>
        </div>
      </Card>

      <Sheet
        open={!!selectedApplication}
        onOpenChange={(open) => !open && setSelectedApplication(null)}
      >
        <SheetContent side="right" className="sm:max-w-[65%] w-full">
          {selectedApplication && (
            <AcceptanceDetailsViewer
              applicationId={selectedApplication.id}
              internshipType={selectedApplication.type}
              onActionComplete={() => setSelectedApplication(null)}
            />
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
